import React, { useState } from 'react';
import { Modal } from '../..';
import {
	Divider,
	Typography,
	Grid,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	ListItem,
	ListItemText
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { withStyles } from '@material-ui/core/styles';
import { styles } from '../MarketingCampaigns.styles';
import { mainColor } from '../../../constants';
import moment from 'moment';
import { CAMPAIGN_ITEMS } from '../../../constants/marketingCampaign';
import { SUITABLE_ORDER_TYPES, ALL_ORDER_TYPES } from '../../../constants/dataConfig';
import { getCreatorLabel } from '../MarketingCampaigns.services';


const StyledAccordion = withStyles({
	root: {
		border: 'none',
		boxShadow: 'none',
		margin: '0 !important',
		padding: '0',
		width: '100%',
		overflow: 'hidden',
		'&:before': { display: 'none' },
		'&$expanded': {
			margin: '0 !important',
			minHeight: 'unset !important'
		},
	},
	expanded: {},
})(Accordion);

const StyledAccordionSummary = withStyles({
	root: {
		minHeight: '45px !important',
		height: '45px',
		padding: '0 12px',
		'&$expanded': { minHeight: '45px !important', height: '45px' },
	},
	content: {
		margin: '0 !important',
		'&$expanded': { margin: '0 !important' },
		alignItems: 'center',
	},
	expandIcon: {
		marginRight: '0px !important',
		transform: 'rotate(0deg)',
		transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
		'&$expanded': {
			transform: 'rotate(180deg)',
		},
		'&:hover': {
			backgroundColor: `rgba(${mainColor.rgb}, 0.5)`
		},
		padding: '7px 7px 7px 7px'
	},
	expanded: {},
})(AccordionSummary);


const DetailsModal = ({
	classes,
	showModal,
	handleClose,
	selectedCampaign,
	shops = {},
}) => {
	const { type, config, dateEnd, dateStart, marketingCampaignKey, createdBy } = selectedCampaign;
	const { title, description, inputDescription, inputText } = CAMPAIGN_ITEMS.filter(item => type === item.id)[0];

	const modalTitle = marketingCampaignKey
		? `Détails de l'offre ${marketingCampaignKey} (${getCreatorLabel(createdBy, shops)})`
		: `Détails de l'offre ${title?.toLowerCase()}`;

	const getDefaultPanel = () => {
		if (inputText) return 'discount';
		return 'threshold';
	};

	const [expandedSection, setExpandedSection] = useState(getDefaultPanel());

	const formatDateTime = (dateString) => {
		if (!dateString) return 'Non spécifié';
		return moment(dateString).format('DD/MM/YYYY [à] HH:mm');
	};

	const handleAccordionChange = (panel) => (event, isExpanded) => {
		setExpandedSection(isExpanded ? panel : null);
	};

	const renderDiscountPanel = () => (
		<>
			<StyledAccordion
				expanded={expandedSection === 'discount'}
				onChange={handleAccordionChange('discount')} style={{ marginRight: '0px' }}
			>
				<StyledAccordionSummary className='p-0'
					expandIcon={<ExpandMoreIcon style={{ color: mainColor.colorBlack }} />}
				>
					<ListItem className="p-0">
						<ListItemText
							primary={<span className={classes.typo}>{inputText}</span>}
							style={{ color: mainColor.colorBlack }}
						/>
					</ListItem>
				</StyledAccordionSummary>
				<AccordionDetails className='p-2'>
					<Grid container spacing={1} alignItems="center" >
						<Grid item xs={9}>
							<Typography variant="body2">{inputDescription}</Typography>
						</Grid>
						<Grid item xs={3}>
							<Typography variant="body2">
								{config.discount?.value} {config.discount?.type === 'percentage' ? '%' : '€'}
							</Typography>
						</Grid>
					</Grid>
				</AccordionDetails>
			</StyledAccordion>
			<Divider className={classes.dividerStyle} />
		</>
	);

	const renderThresholdPanel = () => (
		<>
			<StyledAccordion
				expanded={expandedSection === 'threshold'}
				onChange={handleAccordionChange('threshold')} style={{ marginRight: '0px' }}
			>
				<StyledAccordionSummary className="p-0"
					expandIcon={<ExpandMoreIcon style={{ color: mainColor.colorBlack }} />}
				>
					<ListItem className="p-0">
						<ListItemText
							primary={<span className={classes.typo}>Panier minimum</span>}
							style={{ color: mainColor.colorBlack }}
						/>
					</ListItem>
				</StyledAccordionSummary>
				<AccordionDetails className='p-2'>
					<Grid container spacing={1} alignItems="center" >
						<Grid item xs={9}>
							<Typography variant="body2">
								Pour activer l'offre le client doit dépenser
							</Typography>
						</Grid>
						<Grid item xs={3}>
							<Typography variant="body2">
								{config.threshold} €
							</Typography>
						</Grid>
					</Grid>
				</AccordionDetails>
			</StyledAccordion>
			<Divider className={classes.dividerStyle} />
		</>
	);

	const renderDatesPanel = () => (
		<>
			<StyledAccordion
				expanded={expandedSection === 'dates'}
				onChange={handleAccordionChange('dates')}
			>
				<StyledAccordionSummary className='p-0' expandIcon={<ExpandMoreIcon style={{ color: mainColor.colorBlack }} />}>
					<ListItem className='p-0'>
						<ListItemText style={{ color: mainColor.colorBlack }} primary={<span className={classes.typo}>Dates de l'offre</span>} />
					</ListItem>
				</StyledAccordionSummary>
				<AccordionDetails className='p-2'>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<div style={{ display: 'flex' }}>
							<Typography variant="body2" style={{ minWidth: '220px' }}>
								Date de début le :
							</Typography>
							<Typography variant="body2">
								{formatDateTime(dateStart)}
							</Typography>
						</div>
						<div style={{ display: 'flex' }}>
							<Typography variant="body2" style={{ minWidth: '220px' }}>
								Date de fin le :
							</Typography>
							<Typography variant="body2">
								{formatDateTime(dateEnd)}
							</Typography>
						</div>
					</div>
				</AccordionDetails>
			</StyledAccordion>
		</>
	);

	const orderTypes = config?.orderTypes ?? selectedCampaign?.orderTypes ?? [];
	const effectiveOrderTypes = orderTypes.length > 0 ? orderTypes : ALL_ORDER_TYPES;
	const renderOrderTypesPanel = () => (
		<>
			<StyledAccordion
				expanded={expandedSection === 'orderTypes'}
				onChange={handleAccordionChange('orderTypes')}
			>
				<StyledAccordionSummary className='p-0' expandIcon={<ExpandMoreIcon style={{ color: mainColor.colorBlack }} />}>
					<ListItem className='p-0'>
						<ListItemText style={{ color: mainColor.colorBlack }} primary={<span className={classes.typo}>Mode(s) de vente</span>} />
					</ListItem>
				</StyledAccordionSummary>
				<AccordionDetails className='p-2'>
					<Grid container spacing={1} alignItems="center">
						<Grid item xs={12}>
							<Typography variant="body2">
								{effectiveOrderTypes.map((ot) => SUITABLE_ORDER_TYPES[ot] || ot).join(', ')}
							</Typography>
						</Grid>
					</Grid>
				</AccordionDetails>
			</StyledAccordion>
		</>
	);

	return (
		<Modal
			color="secondary"
			title={modalTitle}
			header
			fixed
			btnTxt="Retour"
			size="xl"
			modalBodyClass={`py-0 ${classes.modalHight}`}
			onClick={handleClose}
			isOpen={showModal}
			handleClose={handleClose}
		>
			<p className='pt-2'>{description}</p>
			{inputText && renderDiscountPanel()}
			{renderThresholdPanel()}
			{renderDatesPanel()}
			{renderOrderTypesPanel()}

		</Modal>
	);
};

export default withStyles(styles)(DetailsModal);
